import { absUrl } from "@/lib/url";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { MonthGrid, CalendarLegend } from "@/components/calendar/MonthGrid";
import { MonthLegendList } from "@/components/calendar/MonthLegendList";
import { PrayerCrossLinkCard } from "@/components/calendar/PrayerCrossLinkCard";
import { AdSlot } from "@/components/AdSlot";
import { PdfExportButton } from "@/components/PdfExportButton";
import {
  parseISO,
  daysInMonth,
  calendarYearPath,
  calendarMonthPath,
  calendarDatePath,
} from "@/lib/calendar/dates";
import {
  getHolidaysForMonth,
  getHolidaysForYear,
  isEstimatedYear,
} from "@/data/holidays";
import { getHariPentingForMonth } from "@/data/hari-penting";
import { gregorianToHijri, HIJRI_MONTHS } from "@/lib/calendar/hijri";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName, translateHariPenting } from "@/i18n/holidays";
import type { Locale } from "@/i18n/types";
import { FeaturedSvg } from "@/components/seo/FeaturedSvg";
import { detectLongWeekends, formatLongWeekendRange, longWeekendStartSlug } from "@/lib/pseo";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { buildMonthSemantics } from "@/lib/calendar/semantics";
import { SemanticSection } from "@/components/seo/SemanticSection";
import { calendarEntities } from "@/lib/seo/entities";

export function MonthPage({ y, m, locale }: { y: number; m: number; locale?: Locale }) {
  return (
    <SiteShell locale={locale}>
      <MonthBody y={y} m={m} />
    </SiteShell>
  );
}

function MonthBody({ y, m }: { y: number; m: number }) {
  const { t, href, locale } = useLocale();
  const monthName = t.common.months[m - 1];
  const holidays = getHolidaysForMonth(y, m);
  const liburList = holidays.filter((h) => h.type === "libur");
  const cutiList = holidays.filter((h) => h.type === "cuti-bersama");
  const hariPenting = getHariPentingForMonth(m);
  const dim = daysInMonth(y, m);

  const prevDate = m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 };
  const nextDate = m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 };

  // Long weekends that touch this month (start, end, or pass through)
  const yearHolidays = getHolidaysForYear(y);
  const monthLongWeekends = detectLongWeekends(y, yearHolidays).filter((lw) => {
    const a = parseISO(lw.startISO)!;
    const b = parseISO(lw.endISO)!;
    return (a.m === m && a.y === y) || (b.m === m && b.y === y);
  });
  const [lwFilter, setLwFilter] = useState<"all" | "lw" | "harp">("all");
  const lwCounts = {
    all: monthLongWeekends.length,
    lw: monthLongWeekends.filter((x) => x.category === "long-weekend").length,
    harp: monthLongWeekends.filter((x) => x.category === "harpitnas").length,
  };
  const filteredLW = monthLongWeekends.filter((lw) =>
    lwFilter === "all"
      ? true
      : lwFilter === "lw"
        ? lw.category === "long-weekend"
        : lw.category === "harpitnas",
  );
  const lwLabels = locale === "en"
    ? { all: "All", lw: "Long weekend", harp: "Harpitnas" }
    : { all: "Semua", lw: "Long weekend", harp: "Harpitnas" };

  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t.common.home, item: absUrl(href("/")) },
        { "@type": "ListItem", position: 2, name: t.common.calendar, item: absUrl(href("/kalender/")) },
        { "@type": "ListItem", position: 3, name: `${y}`, item: absUrl(href(calendarYearPath(y))) },
      {
        "@type": "ListItem",
          position: 4,
        name: `${monthName} ${y}`,
        item: absUrl(href(calendarMonthPath(y, m))),
      },
    ],
  };
  const webPageLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${monthName} ${y}`,
    inLanguage: locale === "en" ? "en" : "id-ID",
    isPartOf: { "@type": "WebSite", name: "Hari.co.id", url: "https://hari.co.id/" },
    about: calendarEntities(),
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1"] },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageLd) }}
      />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">
          {t.common.home}
        </Link>{" "}
        /{" "}
        <Link to={href("/kalender/") as never} className="hover:text-primary">
          {t.common.calendar}
        </Link>{" "}
        /{" "}
        <Link to={href(calendarYearPath(y)) as never} className="hover:text-primary">
          {y}
        </Link>{" "}
        / {monthName} {y}
      </nav>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl tracking-tight md:text-5xl">
            {t.common.calendar} {monthName} {y}
          </h1>
          <p className="mt-1 text-muted-foreground">
            {t.common.daysCount(dim)} • {t.common.summaryHoliday(liburList.length, cutiList.length)}
            {isEstimatedYear(y) && (
              <span className="ml-2 rounded-md bg-muted px-2 py-0.5 text-xs">
                {t.common.estimatedData}
              </span>
            )}
          </p>
        </div>
        <FeaturedSvg
          variant="month"
          title={`${monthName} ${y}`}
          subtitle={`${t.common.daysCount(dim)} • ${t.common.summaryHoliday(liburList.length, cutiList.length)}`}
          badge={String(m).padStart(2, "0")}
          locale={locale}
          className="hidden md:block md:w-72"
        />
        <div className="flex items-center gap-3">
          <CalendarLegend />
          <PdfExportButton mode="month" year={y} month={m} allowSwitch />
        </div>
      </header>

      <div className="space-y-6">
        <div className="mx-auto w-full max-w-5xl space-y-4">
          <MonthGrid year={y} month={m} showSubLabels />
          <div className="flex justify-between text-sm">
            <Link
              to={href(calendarMonthPath(prevDate.y, prevDate.m)) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              ← {t.common.months[prevDate.m - 1]} {prevDate.y}
            </Link>
            <Link
              to={href(calendarMonthPath(nextDate.y, nextDate.m)) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              {t.common.months[nextDate.m - 1]} {nextDate.y} →
            </Link>
          </div>
          <MonthLegendList
            year={y}
            month={m}
            holidays={holidays}
            hariPenting={hariPenting}
          />

          <div className="rounded-lg border border-border bg-card p-3">
            <p className="mb-2 text-xs font-semibold text-muted-foreground">{t.common.selectMonth} {y}</p>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((mi) => (
                <Link
                  key={mi}
                  to={href(calendarMonthPath(y, mi)) as never}
                  className={"rounded-md border border-border px-2 py-0.5 text-xs hover:bg-accent " + (mi === m ? "bg-accent font-semibold" : "")}
                >
                  {t.common.months[mi - 1].slice(0, 3)}
                </Link>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <Link to={href(calendarYearPath(y)) as never} className="text-primary hover:underline">{t.common.calendar} {y}</Link>
              <Link to={href(`/tanggal-merah/${y}`) as never} className="text-primary hover:underline">{t.common.redDates} {y}</Link>
              <Link to={href(`/libur-nasional/${y}`) as never} className="text-primary hover:underline">{t.common.nationalHolidays} {y}</Link>
              <Link to={href(`/cuti-bersama/${y}`) as never} className="text-primary hover:underline">{t.common.jointLeave} {y}</Link>
              {(() => {
                const mid = gregorianToHijri(y, m, 15);
                return (
                  <Link
                    to={href(`/kalender-hijriah/${mid.year}/${mid.month}/`) as never}
                    className="text-primary hover:underline"
                  >
                    {t.common.viewInHijri}: {HIJRI_MONTHS[mid.month - 1]} {mid.year} H
                  </Link>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="mb-2 font-semibold">
              {t.common.nationalHolidays} {monthName} {y}
            </h2>
            {liburList.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t.home.noHoliday}
              </p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {liburList.map((h) => {
                  const p = parseISO(h.date)!;
                  return (
                    <li key={`${h.date}-${h.name}`}>
                      <Link
                        to={href(calendarDatePath(p.y, p.m, p.d)) as never}
                        className="hover:text-primary"
                      >
                        <span className="font-medium text-red-date">
                          {p.d} {t.common.months[p.m - 1]}
                        </span>{" "}
                        - {translateHolidayName(h.name, locale)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-2 font-semibold">{t.common.jointLeave}</h2>
              {cutiList.length === 0 ? (
                <p className="text-sm text-muted-foreground">-</p>
              ) : (
              <ul className="space-y-1.5 text-sm">
                {cutiList.map((h) => {
                  const p = parseISO(h.date)!;
                  return (
                    <li key={`${h.date}-${h.name}`}>
                      <Link
                        to={href(calendarDatePath(p.y, p.m, p.d)) as never}
                        className="hover:text-primary"
                      >
                        <span className="font-medium text-cuti-bersama">
                          {p.d} {t.common.months[p.m - 1]}
                        </span>{" "}
                        - {translateHolidayName(h.name, locale)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              )}
            </div>
        </div>

        {hariPenting.length > 0 && (
          <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-2 font-semibold">{t.common.importantDays}</h2>
              <ul className="space-y-1.5 text-sm">
                {hariPenting.map((h) => {
                  const [mm, dd] = h.monthDay.split("-").map((n) => parseInt(n, 10));
                  return (
                    <li key={`${h.monthDay}-${h.name}`}>
                      <span className="font-medium text-hari-penting">
                        {dd} {t.common.months[mm - 1]}
                      </span>{" "}
                      -{" "}
                      <Link
                        to={`/hari/${h.slug}` as never}
                        className="hover:text-primary"
                      >
                        {translateHariPenting(h.name, locale)}
                      </Link>{" "}
                      <Link
                        to={href(calendarDatePath(y, mm, dd)) as never}
                        className="text-xs text-muted-foreground hover:text-primary"
                      >
                        (detail tanggal)
                      </Link>
                    </li>
                  );
                })}
              </ul>
          </div>
        )}

        {monthLongWeekends.length > 0 && (
          <section className="rounded-lg border border-border bg-card p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">
                {t.common.longWeekend} & Harpitnas {monthName} {y}
              </h2>
              <div className="inline-flex rounded-md border border-border bg-background p-0.5 text-xs">
                {(["all", "lw", "harp"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setLwFilter(opt)}
                    className={cn(
                      "rounded px-2.5 py-1 font-medium transition-colors",
                      lwFilter === opt
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-accent",
                    )}
                  >
                    {lwLabels[opt]} <span className="ml-0.5 opacity-70">({lwCounts[opt]})</span>
                  </button>
                ))}
              </div>
            </div>
            <p className="mb-3 text-sm text-muted-foreground">{t.longWeekend.desc}</p>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-3 py-2">{locale === "en" ? "Type" : "Tipe"}</th>
                    <th className="px-3 py-2">{t.longWeekend.range}</th>
                    <th className="px-3 py-2">{t.longWeekend.total}</th>
                    <th className="px-3 py-2">{t.longWeekend.triggers}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLW.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">
                        {locale === "en" ? "No items for this filter." : "Tidak ada untuk filter ini."}
                      </td>
                    </tr>
                  )}
                  {filteredLW.map((lw) => (
                    <tr key={lw.startISO} className="border-t border-border">
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            "rounded-md px-2 py-0.5 text-xs font-medium",
                            lw.category === "harpitnas"
                              ? "bg-cuti-bersama/10 text-cuti-bersama"
                              : "bg-red-date/10 text-red-date",
                          )}
                        >
                          {lw.category === "harpitnas" ? "Harpitnas" : "Long weekend"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={href(`/kalender/${longWeekendStartSlug(lw)}`) as never}
                          className="text-primary hover:underline"
                        >
                          {formatLongWeekendRange(lw, locale)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 font-medium">{t.common.daysCount(lw.totalDays)}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {lw.triggers.map((tr) => translateHolidayName(tr.name, locale)).join(", ") || t.longWeekend.fallback}
                        {lw.harpitISO && (
                          <div className="mt-0.5 text-xs">
                            <span className="font-medium text-cuti-bersama">
                              {locale === "en" ? "Take leave: " : "Cuti: "}
                            </span>
                            {lw.harpitISO.slice(8, 10)}/{lw.harpitISO.slice(5, 7)}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <AdSlot size="rectangle" position="sidebar" />
      </div>
      {locale === "id" && (
        <div className="mt-6">
          <PrayerCrossLinkCard mode="month" y={y} m={m} />
        </div>
      )}
      {locale === "id" && (
        <div className="mt-6">
          <SemanticSection
            block={buildMonthSemantics(y, m)}
            heading={`Tentang ${monthName} ${y}`}
          />
        </div>
      )}
    </>
  );
}